// import * as Sentry from '@sentry/react';
import { useEffect, useMemo } from 'react';
import { useQuery, UseQueryOptions } from 'react-query';
import { queryFnFetchErrorWrapper, throwFetchError } from 'src/api/utils/errorHandlers';
import { mergeQueryOptions, retryOnTransient } from 'src/api/utils/helpers';
import { queryNames } from 'src/api/utils/queryNames';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { CustomQueryOptions, FetchError } from 'src/types/TApiQueries';
import { AllCollectionsResponse } from 'src/types/TCollection';
import {
  getCollection,
  getAllCollections,
  CollectionQuery,
  searchCollections,
  getCollectionCount,
  CollectionCountResponse,
  searchCollection,
  CollectionSearchQuery,
  CollectionSearchResponse,
} from '../functions/collectionsApi';
import { isValidSort } from '../utils/search';

export function useCollectionQuery(
  collectionId?: number,
  options?: CustomQueryOptions,
  queryOptions?: UseQueryOptions<any[], FetchError>,
) {
  const actualQueryOptions = mergeQueryOptions(options, queryOptions);
  const accessToken = useAccessToken();

  const queryFn = useMemo(
    () => queryFnFetchErrorWrapper(() => getCollection(accessToken, collectionId)),
    [collectionId, accessToken],
  );

  const query = useQuery<any, FetchError>([queryNames.collection, collectionId], queryFn, actualQueryOptions);

  if (options?.handleErrors && query.status === 'error') {
    if (query.isRefetchError) {
      // Sentry.captureMessage('Failed refetching collection', Sentry.Severity.Error);
    } else {
      throwFetchError(query.error);
    }
  }

  return query;
}

// Schema-aware search/pagination within a single collection. Drives the
// collection detail page for both the unfiltered view (empty query =>
// all assets, paginated) and filtered views (e.g. status=approved). The
// query key includes the serialised filters so each page/filter combo is
// cached independently.
export function useCollectionSearchQuery(
  collectionId: number,
  searchQuery: CollectionSearchQuery,
  queryOptions?: UseQueryOptions<CollectionSearchResponse, FetchError>,
) {
  const accessToken = useAccessToken();

  const queryFn = useMemo(
    () => queryFnFetchErrorWrapper(() => searchCollection(accessToken, collectionId, searchQuery)),
    [accessToken, collectionId, searchQuery],
  );

  return useQuery<CollectionSearchResponse, FetchError>(
    [queryNames.collection, collectionId, 'search', searchQuery],
    queryFn,
    {
      keepPreviousData: true,
      staleTime: 0,
      enabled: !Number.isNaN(collectionId),
      ...queryOptions,
    },
  );
}

// Count-only counterpart of `useAllCollectionsQuery`. Hits the same
// endpoint but with `count_only=y`, so the backend skips hydrating
// collection rows and just returns the total. Cached under a separate
// query key so the regular collections page (which needs the full
// list) is not affected.
export function useCollectionCountQuery(
  queryOptions?: UseQueryOptions<CollectionCountResponse, FetchError>,
) {
  const accessToken = useAccessToken();

  const queryFn = useMemo(
    () => queryFnFetchErrorWrapper(() => getCollectionCount(accessToken)),
    [accessToken],
  );

  return useQuery<CollectionCountResponse, FetchError>(
    [queryNames.collection, 'count'],
    queryFn,
    {
      staleTime: 60_000,
      retry: retryOnTransient,
      ...queryOptions,
    },
  );
}

export function useAllCollectionsQuery(
  options?: CustomQueryOptions,
  queryOptions?: UseQueryOptions<AllCollectionsResponse, FetchError>,
) {
  const actualQueryOptions = mergeQueryOptions(options, queryOptions);
  const accessToken = useAccessToken();

  const queryFn = useMemo(() => queryFnFetchErrorWrapper(() => getAllCollections(accessToken)), [accessToken]);

  const query = useQuery<AllCollectionsResponse, FetchError>(
    [queryNames.collection, 'all'],
    queryFn,
    actualQueryOptions,
  );

  if (options?.handleErrors && query.status === 'error') {
    if (query.isRefetchError) {
      // Sentry.captureMessage('Failed refetching collection', Sentry.Severity.Error);
    } else {
      throwFetchError(query.error);
    }
  }

  return query;
}

const sortConfig = {
  delimiter: ' ',
  validFields: ['last_update', 'name', 'group_access'],
  validDirections: ['asc', 'desc'],
};

export function useSearchCollectionsQuery(
  searchQuery: CollectionQuery,
  options?: CustomQueryOptions,
  queryOptions?: UseQueryOptions<AllCollectionsResponse, FetchError>,
) {
  const actualQueryOptions = mergeQueryOptions(options, queryOptions);
  const accessToken = useAccessToken();

  const queryFn = useMemo(
    () => queryFnFetchErrorWrapper(() => searchCollections(accessToken, searchQuery)),
    [accessToken, searchQuery],
  );

  const enabled = useMemo(() => {
    if (!searchQuery?.sort_by) return false;

    const { sort_by } = searchQuery;

    return sort_by.every((sort) => isValidSort(sort, sortConfig));
  }, [searchQuery]);

  const query = useQuery<AllCollectionsResponse, FetchError>(
    [queryNames.collection, 'search'],
    queryFn,
    {
      ...actualQueryOptions,
      enabled,
      staleTime: 0,
    },
  );

  if (options?.handleErrors && query.status === 'error') {
    if (query.isRefetchError) {
      // Sentry.captureMessage('Failed refetching collection', Sentry.Severity.Error);
    } else {
      throwFetchError(query.error);
    }
  }

  useEffect(() => {
    query.refetch();
  }, [searchQuery]);

  return query;
}
